/**
 * The topic name an action would read off a file name with
 * `TOPIC_NAME_FROM_FILE_NAME_REGEX`, checked and previewed in the browser.
 *
 * `csv_ingestion` and `parquet_ingestion` both run the pattern with Python's
 * `re.match`, which matches only from the start of the file name, and replace
 * every `.` in the extracted name with `_`. Python spells named groups
 * `(?P<name>...)` and back-references `(?P=name)`, where JavaScript spells
 * them `(?<name>...)` and `\k<name>`, so the stored pattern is translated
 * before it is compiled here; what is stored is left as written.
 */

export const TOPIC_NAME_GROUP = "topic_name";

export type TopicNamePreview =
  { ok: true; topicName: string } | { ok: false; error: string };

type CompiledPattern =
  { ok: true; regex: RegExp } | { ok: false; error: string };

/** Python's global flags that JavaScript has, mapped to JavaScript's letters. */
const JS_FLAGS: Readonly<Record<string, string>> = { i: "i", m: "m", s: "s" };
/** Python's global flags that change nothing for a `str` pattern in JavaScript. */
const NO_OP_FLAGS = new Set(["a", "u"]);

/** A Python pattern in JavaScript's spelling of named groups and anchors. */
export function pythonRegexToJs(pattern: string): string {
  return (
    pattern
      .replace(/\(\?P<([A-Za-z_][A-Za-z0-9_]*)>/g, "(?<$1>")
      .replace(/\(\?P=([A-Za-z_][A-Za-z0-9_]*)\)/g, "\\k<$1>")
      // `\A` and `\Z` are Python's start and end of the string; an even run of
      // backslashes before them is escaped backslashes, not the escape.
      .replace(/(?<!\\)((?:\\\\)*)\\A/g, "$1^")
      .replace(/(?<!\\)((?:\\\\)*)\\Z/g, "$1$")
  );
}

/**
 * Splits a leading `(?flags)` group off a Python pattern. Null when the
 * pattern uses a flag JavaScript cannot apply, such as `x`.
 */
function leadingFlags(pattern: string): { flags: string; rest: string } | null {
  const found = /^\(\?([a-zA-Z]+)\)/.exec(pattern);
  if (found === null) {
    return { flags: "", rest: pattern };
  }
  let flags = "";
  for (const flag of found[1]) {
    if (NO_OP_FLAGS.has(flag)) {
      continue;
    }
    if (!Object.hasOwn(JS_FLAGS, flag)) {
      return null;
    }
    flags += JS_FLAGS[flag];
  }
  return { flags, rest: pattern.slice(found[0].length) };
}

/**
 * `pattern` compiled to match only at the start of a string, as Python's
 * `re.match` does, or why it can't be previewed.
 */
function compileTopicNamePattern(pattern: string): CompiledPattern {
  const invalid = {
    ok: false,
    error:
      "This pattern can't be previewed here. The action checks it when it runs.",
  } as const;
  const split = leadingFlags(pattern);
  if (split === null) {
    return invalid;
  }
  const translated = pythonRegexToJs(split.rest);
  let regex: RegExp;
  try {
    // The sticky flag anchors the match at `lastIndex`, which starts at 0.
    regex = new RegExp(translated, `${split.flags}y`);
  } catch {
    return invalid;
  }
  if (!translated.includes(`(?<${TOPIC_NAME_GROUP}>`)) {
    return { ok: false, error: missingGroupError() };
  }
  return { ok: true, regex };
}

/**
 * Why the actions would refuse `pattern` on every file, or undefined. Only a
 * missing `topic_name` group is certain here: Python accepts syntax that
 * JavaScript cannot compile, such as the verbose flag, so a pattern this
 * browser cannot compile is left for the action to judge.
 */
export function topicNamePatternError(pattern: string): string | undefined {
  return pattern.includes(`(?P<${TOPIC_NAME_GROUP}>`)
    ? undefined
    : missingGroupError();
}

function missingGroupError(): string {
  return `The pattern needs a group named ${TOPIC_NAME_GROUP}, like (?P<${TOPIC_NAME_GROUP}>.+)\\.csv.`;
}

/** What `pattern` makes of `fileName`, or why it makes nothing of it. */
export function previewTopicName(
  pattern: string,
  fileName: string,
): TopicNamePreview {
  const compiled = compileTopicNamePattern(pattern);
  if (!compiled.ok) {
    return compiled;
  }
  const topicName = compiled.regex.exec(fileName)?.groups?.[TOPIC_NAME_GROUP];
  if (topicName === undefined || topicName === "") {
    return { ok: false, error: "The pattern does not match this file name." };
  }
  return { ok: true, topicName: topicName.replaceAll(".", "_") };
}
